import axios from 'axios';
import moment from 'moment-timezone';
import { createSlice } from '@reduxjs/toolkit';

import { UIHelpers } from '../helpers';
import OutboundPostMessageService from '../services/OutboundPostMessageService';
import WebSocketService from '../services/WebSocketService';
import * as UserActionTypes from '../constants/UserActionTypes';

// SLICE

const uiSlice = createSlice({
  name: 'UI',
  initialState: {
    profileOpen: false,
    convoOpen: false,
    navigationOpen: false,
    usersTyping: [],
    inboxSections: {},
    error: null,
    formInProgress: false,
    modalFormInProgress: false,
    isSessionTimeoutModalVisible: false,
    isEmailModalOpen: false,
    recordLock: {},
    showSystemAlert: true,
    fileUploadPostProcessing: [],
  },
  reducers: {
    setProfileOpen: (state, action) =>
      ({
        ...state,
        profileOpen: action.payload,
      }),
    setConvoOpen: (state, action) =>
      ({
        ...state,
        convoOpen: action.payload,
      }),
    setNavigationOpen: (state, action) =>
      ({
        ...state,
        navigationOpen: action.payload,
      }),
    addTypingUser: (state, action) =>
      ({
        ...state,
        // fail safe so we don't end up with the same user multiple times or old typing states
        usersTyping: state.usersTyping.concat(action.payload).filter(u => moment.utc().diff(moment.utc(u.date)) <= WebSocketService.typingTimeout),
      }),
    removeTypingUser: (state, action) =>
      ({
        ...state,
        usersTyping: state.usersTyping.filter(u => (
          u.typingUserId !== action.payload.typingUserId
        )),
      }),
    setError: (state, action) =>
      ({
        ...state,
        error: action.payload,
        formInProgress: false,
        modalFormInProgress: false,
      }),
    receiveInboxSections: (state, action) =>
      ({
        ...state,
        inboxSections: action.payload,
      }),
    clearRecordLock: state =>
      ({
        ...state,
        recordLock: {},
      }),
    setSystemAlertVisibility: (state, action) =>
      ({
        ...state,
        showSystemAlert: action.payload,
      }),
    setFormInProgress: (state, action) => ({ ...state, formInProgress: action.payload }),
    setModalFormInProgress: (state, action) => ({ ...state, modalFormInProgress: action.payload }),
    setSessionTimeoutModalVisability: (state, action) => ({ ...state, isSessionTimeoutModalVisible: action.payload }),
    toggleEmailModal: (state, action) => ({ ...state, isEmailModalOpen: action.payload }),
    setFileUploadPostProcessingInProgress: (state, action) => ({
      ...state,
      fileUploadPostProcessing: [...state.fileUploadPostProcessing, action.payload.key],
    }),
    receiveWebSocketFileUploadPostProcessing: (state, action) => ({
      ...state,
      fileUploadPostProcessing: state.fileUploadPostProcessing.filter(key => key !== action.payload.key),
    }),
  },
  extraReducers: {
    [UserActionTypes.receiveContactEditFormView]: (state, action) =>
      ({
        ...state,
        recordLock: action.payload.recordLock,
      }),
  },
});

export default uiSlice.reducer;

// ACTIONS

export const {
  addTypingUser,
  clearRecordLock,
  setSystemAlertVisibility,
  receiveInboxSections,
  receiveInboxUnreadUserIds,
  receivePageData,
  removeTypingUser,
  requestPageData,
  setConvoOpen,
  setError,
  setFormInProgress,
  setModalFormInProgress,
  setNavigationOpen,
  setProfileOpen,
  setSessionTimeoutModalVisability,
  setNavigationLocked,
  toggleEmailModal,
  receiveWebSocketFileUploadPostProcessing,
  setFileUploadPostProcessingInProgress,
} = uiSlice.actions;


// THUNKS -- ASYNC ACTION CREATORS

export function fetchInboxSections() {
  return dispatch =>
    axios.get('/inboxSections')
      .then((response) => {
        dispatch(receiveInboxSections(response.data));

        // Update native app badge count
        const badgeCount = UIHelpers.getTotalUnreadCount();
        OutboundPostMessageService.postMessage({
          type: 'setBadgeCount',
          data: { badgeCount },
        });
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));
      });
}

export function showSessionTimeoutModal() {
  return dispatch => dispatch(setSessionTimeoutModalVisability(true));
}

export function hideSessionTimeoutModal() {
  return dispatch => dispatch(setSessionTimeoutModalVisability(false));
}

export function setSystemAlert(payload) {
  return dispatch => dispatch(setSystemAlertVisibility(payload));
}

export function createLock(payload) {
  return axios.post('/writeControl/lock', payload)
    .catch(err => console.error(err)); // eslint-disable-line no-console
}

export function removeLock(payload) {
  return dispatch =>
    axios.post('/writeControl/unlock', payload)
      .then(() => {
        dispatch(clearRecordLock());
      })
      .catch((err) => {
        console.error(err.response); // eslint-disable-line no-console

        dispatch(setError(err.response));
      });
}
